// File workspace service - talks to the backend /files API for the RightSidebar
// "File" panel. Mirrors conversationService.ts style: plain fetch, typed shapes,
// no axios. Auth header is `${tokenType} ${token}` from localStorage, matching
// every other service in this app.
import config from '../../config';

const BASE_URL = config.apiUrl;

export type FileStatus = 'processing' | 'ready' | 'error';

// The canonical file record returned by every /files endpoint.
export interface FileMeta {
  id: number;
  filename: string;
  mime: string;
  size: number;      // bytes
  status: FileStatus;
  chars: number;
  created_at: string; // ISO string
  // Short user-safe blurb: extracted-text preview when ready, or the failure
  // reason (e.g. "Ekstraksi gagal: ...") when status === 'error'. Rendered as
  // the row tooltip for failed files. May be absent/null on older rows.
  summary?: string | null;
  // Whether this file is selected as context for the queried conversation.
  // Always false when the library is listed without a conversation_id.
  selected: boolean;
  project_id?: number | null;
  // Folder path inside this file's library or project. Null = root.
  folder?: string | null;
}

export interface FileLibrary {
  files: FileMeta[];
  folders: string[];
  used_bytes: number;
  max_bytes: number | null;
  file_count: number;
  max_files: number;
  personal_used_bytes: number;
  personal_max_bytes: number;
}

export const EMPTY_LIBRARY: FileLibrary = {
  files: [],
  folders: [],
  used_bytes: 0,
  max_bytes: null,
  file_count: 0,
  max_files: 200,
  personal_used_bytes: 0,
  personal_max_bytes: 100 * 1024 * 1024,
};

// Chat baru belum punya conversation_id, jadi centang di panel File disimpan
// di sini lalu diterapkan ke percakapan yang lahir dari pesan pertama.
export const PENDING_FILE_SELECTION_KEY = 'pendingFileSelection';
export const FILES_CHANGED_EVENT = 'powerai:files-changed';

export function readPendingFileSelection(): number[] {
  try {
    const raw = JSON.parse(localStorage.getItem(PENDING_FILE_SELECTION_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .map((n) => (typeof n === 'number' ? n : Number(n)))
      .filter((n) => Number.isInteger(n) && n > 0);
  } catch {
    return [];
  }
}

export function writePendingFileSelection(ids: number[]): void {
  localStorage.setItem(PENDING_FILE_SELECTION_KEY, JSON.stringify(ids));
}

export function clearPendingFileSelection(): void {
  localStorage.removeItem(PENDING_FILE_SELECTION_KEY);
}

export function notifyFilesChanged(): void {
  window.dispatchEvent(new Event(FILES_CHANGED_EVENT));
}

export type SpreadsheetSheet = {
  name: string;
  rows: string[][];
  total_rows: number;
  total_cols: number;
  truncated?: boolean;
  fills?: (string | null)[][];
  bolds?: boolean[][];
};

export type SlideShape =
  | { type: 'text'; x: number; y: number; w: number; h: number; text: string; pt?: number }
  | { type: 'table'; x: number; y: number; w: number; h: number; rows: string[][] }
  | { type: 'image'; x: number; y: number; w: number; h: number; src?: string };

export type SlideBlock =
  | { type: 'text' | 'notes'; text: string }
  | { type: 'table'; rows: string[][] };

export type FilePreview =
  | { kind: 'image' | 'pdf'; url: string; filename: string; mime?: string }
  | { kind: 'text'; text: string; filename: string; chars?: number; truncated?: boolean }
  | { kind: 'spreadsheet'; filename: string; sheets: SpreadsheetSheet[]; truncated?: boolean }
  | { kind: 'html'; filename: string; html: string }
  | { kind: 'document'; filename: string; html: string }
  | {
      kind: 'slides';
      filename: string;
      slides: {
        index: number;
        ratio?: number;
        shapes?: SlideShape[];
        blocks: SlideBlock[];
        notes?: string | null;
      }[];
      truncated?: boolean;
    }
  | { kind: 'other'; url: string; filename: string; summary?: string | null };

// Thrown on 403/413 (and any other non-2xx with a JSON `detail`). The caller
// surfaces `detail` verbatim as inline error text.
export class FileWorkspaceError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'FileWorkspaceError';
    this.status = status;
    this.detail = detail;
  }
}

// Thrown on 401. localStorage is already cleared by the time this surfaces, so
// the caller only needs to redirect to /login (existing app-wide pattern).
export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType');
  return { Authorization: `${tokenType} ${token}` };
}

// Shared response gate: 401 → clear + UnauthorizedError; other non-2xx → pull
// `detail` out of the JSON body and raise FileWorkspaceError.
async function ensureOk(res: Response): Promise<void> {
  if (res.status === 401) {
    localStorage.clear();
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    let detail = `Request gagal (${res.status})`;
    try {
      const data = await res.json();
      if (data && typeof data.detail === 'string' && data.detail) {
        detail = data.detail;
      }
    } catch {
      // body wasn't JSON - keep the generic message
    }
    throw new FileWorkspaceError(res.status, detail);
  }
}

// GET /files[?conversation_id=N][&project_id=N] - the user's WHOLE library.
// With a conversation_id, each row's `selected` reflects that conversation;
// without one, every row comes back selected=false.
// `projectId` (Teams phase 3) switches the scope to a project's own files, which
// live under a team prefix and are visible to everyone who can see the project.
// Personal-library callers omit it and behave exactly as before.
export async function listLibrary(
  conversationId: number | null,
  projectId?: number | null
): Promise<FileLibrary> {
  const params = new URLSearchParams();
  if (conversationId != null) params.set('conversation_id', String(conversationId));
  if (projectId != null) params.set('project_id', String(projectId));
  const qs = params.toString();
  const res = await fetch(`${BASE_URL}/files${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  await ensureOk(res);
  const data = await res.json();
  const files = Array.isArray(data?.files) ? (data.files as FileMeta[]) : [];
  const folders = Array.isArray(data?.folders)
    ? (data.folders as unknown[]).filter((p): p is string => typeof p === 'string')
    : [];
  return {
    files,
    folders,
    used_bytes: Number(data?.used_bytes) || 0,
    max_bytes: data?.max_bytes == null ? null : Number(data.max_bytes) || 0,
    file_count: Number(data?.file_count) || files.length,
    max_files: Number(data?.max_files) || 200,
    personal_used_bytes: Number(data?.personal_used_bytes) || 0,
    personal_max_bytes: Number(data?.personal_max_bytes) || EMPTY_LIBRARY.personal_max_bytes,
  };
}

export async function listFiles(
  conversationId: number | null,
  projectId?: number | null
): Promise<FileMeta[]> {
  return (await listLibrary(conversationId, projectId)).files;
}

// POST /files/upload - multipart FormData { file[, conversation_id][, project_id] }
// With a conversation_id the server auto-selects the upload into it (trust the
// returned `selected`); without one it lands in the library unselected.
// `projectId` (Teams phase 3) files the upload under the project instead of the
// personal library; existing call sites pass two arguments and are unaffected.
// Note: do NOT set Content-Type; the browser sets the multipart boundary.
export async function uploadFile(
  conversationId: number | null,
  file: File,
  projectId?: number | null,
  folder?: string | null
): Promise<FileMeta> {
  const form = new FormData();
  form.append('file', file);
  if (conversationId != null) form.append('conversation_id', String(conversationId));
  if (projectId != null) form.append('project_id', String(projectId));
  if (folder) form.append('folder', folder);
  const res = await fetch(`${BASE_URL}/files/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  await ensureOk(res);
  return (await res.json()) as FileMeta;
}

// POST /files/{id}/selection - { conversation_id, selected } → { ok, selected }.
// Toggles whether a library file is used as context for a conversation. Returns
// the server-confirmed selected flag. Raises FileWorkspaceError on the 20-file
// cap (403 with `detail` = "Maksimal 20 file aktif per percakapan").
export async function setFileSelection(
  fileId: number,
  conversationId: number,
  selected: boolean
): Promise<boolean> {
  const res = await fetch(`${BASE_URL}/files/${fileId}/selection`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversation_id: conversationId, selected }),
  });
  await ensureOk(res);
  const data = await res.json();
  return typeof data?.selected === 'boolean' ? data.selected : selected;
}

// POST /files/{id}/reprocess → FileMeta. Re-runs extraction on a FAILED
// ("error") file from its stored original, returning the row flipped back to
// "processing". Raises FileWorkspaceError on 409 (file not in the failed state)
// or if the stored original is gone (500) - the caller reverts + surfaces detail.
export async function reprocessFile(fileId: number): Promise<FileMeta> {
  const res = await fetch(`${BASE_URL}/files/${fileId}/reprocess`, {
    method: 'POST',
    headers: authHeaders(),
  });
  await ensureOk(res);
  return (await res.json()) as FileMeta;
}

// DELETE /files/{id} → { ok: true }
export async function deleteFile(fileId: number): Promise<void> {
  const res = await fetch(`${BASE_URL}/files/${fileId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

// GET /files/{id}/download → { url } (presigned; opened in a new tab by caller)
export async function patchFile(
  fileId: number,
  body: { filename?: string; folder?: string | null }
): Promise<FileMeta> {
  const res = await fetch(`${BASE_URL}/files/${fileId}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  await ensureOk(res);
  return (await res.json()) as FileMeta;
}

export async function createFolder(
  path: string,
  projectId?: number | null
): Promise<string> {
  const res = await fetch(`${BASE_URL}/files/folders`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      path,
      ...(projectId != null ? { project_id: projectId } : {}),
    }),
  });
  await ensureOk(res);
  const data = await res.json();
  return typeof data?.path === 'string' ? data.path : path;
}

export async function deleteFolder(
  path: string,
  projectId?: number | null
): Promise<void> {
  const params = new URLSearchParams({ path });
  if (projectId != null) params.set('project_id', String(projectId));
  const res = await fetch(`${BASE_URL}/files/folders?${params.toString()}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

export async function getPreview(fileId: number): Promise<FilePreview> {
  const res = await fetch(`${BASE_URL}/files/${fileId}/preview`, {
    headers: authHeaders(),
  });
  await ensureOk(res);
  return (await res.json()) as FilePreview;
}

export async function getFileBytes(fileId: number): Promise<ArrayBuffer> {
  const res = await fetch(`${BASE_URL}/files/${fileId}/content`, {
    headers: authHeaders(),
  });
  await ensureOk(res);
  return res.arrayBuffer();
}

export type OfficeEngine = 'xlsx' | 'pptx';

export function officeEngine(mime: string, filename: string): OfficeEngine | null {
  const m = (mime || '').toLowerCase();
  const ext = (filename.split('.').pop() || '').toLowerCase();
  if (ext === 'pptx' || m.includes('presentationml') || m.includes('powerpoint')) {
    return 'pptx';
  }
  if (
    ['xlsx', 'xlsm', 'xls', 'csv'].includes(ext) ||
    m.includes('spreadsheet') ||
    m.includes('excel') ||
    m === 'text/csv' ||
    m === 'application/csv'
  ) {
    return 'xlsx';
  }
  return null;
}

export function childFolders(all: string[], current: string | null): string[] {
  const prefix = current ? `${current}/` : '';
  const names = new Set<string>();
  for (const p of all) {
    if (!p || p === current) continue;
    if (current && !p.startsWith(prefix)) continue;
    const rest = current ? p.slice(prefix.length) : p;
    if (!rest) continue;
    names.add(rest.split('/')[0]);
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b, 'id'));
}

export function filesInFolder(files: FileMeta[], current: string | null): FileMeta[] {
  const folder = current || null;
  return files.filter((f) => (f.folder || null) === folder);
}

export function joinFolder(current: string | null, name: string): string {
  return current ? `${current}/${name}` : name;
}

export function folderDepth(path: string | null): number {
  if (!path) return 0;
  return path.split('/').filter(Boolean).length;
}

export function parentFolder(current: string | null): string | null {
  if (!current) return null;
  const i = current.lastIndexOf('/');
  return i === -1 ? null : current.slice(0, i);
}

export async function getDownloadUrl(fileId: number): Promise<string> {
  const res = await fetch(`${BASE_URL}/files/${fileId}/download`, {
    headers: authHeaders(),
  });
  await ensureOk(res);
  const data = await res.json();
  return typeof data?.url === 'string' ? data.url : '';
}

// Human file size with Indonesian comma decimals, e.g. 2_516_582 → "2,4 MB".
// Bytes render as a whole number; KB and up render with one decimal.
export function prettySize(bytes: number): string {
  if (!bytes || bytes < 0 || !Number.isFinite(bytes)) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  if (unit === 0) {
    return `${Math.round(value)} B`;
  }
  const oneDecimal = (Math.round(value * 10) / 10).toFixed(1).replace('.', ',');
  return `${oneDecimal} ${units[unit]}`;
}

// Which glyph family to draw for a given mime/filename.
export type FileFamily = 'doc' | 'sheet' | 'pdf' | 'img' | 'zip' | 'other';

export function fileFamily(mime: string, filename: string): FileFamily {
  const m = (mime || '').toLowerCase();
  const ext = (filename.split('.').pop() || '').toLowerCase();

  if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic'].includes(ext)) {
    return 'img';
  }
  if (m === 'application/pdf' || ext === 'pdf') {
    return 'pdf';
  }
  if (
    m.includes('spreadsheet') ||
    m.includes('excel') ||
    m.includes('csv') ||
    ['xlsx', 'xls', 'csv', 'ods'].includes(ext)
  ) {
    return 'sheet';
  }
  if (
    m.includes('zip') ||
    m.includes('compressed') ||
    m.includes('tar') ||
    m.includes('rar') ||
    m.includes('7z') ||
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)
  ) {
    return 'zip';
  }
  if (
    m.includes('word') ||
    m.includes('document') ||
    m.startsWith('text/') ||
    ['doc', 'docx', 'txt', 'md', 'rtf', 'odt'].includes(ext)
  ) {
    return 'doc';
  }
  return 'other';
}
